import type { BuyerSendVersionDto } from "@/api/buyerSendVersions";
import { SubTable, Timestamp, type SubTableColumn } from "@/design-system/components/ui";
import {
  Blank,
  CarriedInBadge,
  combinedOthersLabel,
  pathLabel,
  versionLabel,
} from "./sendVersionBits";

/**
 * The intro email's history, as a DENSE TABLE.
 *
 * <p><b>Why a table here and cards for the package.</b> An intro email is text
 * and nothing else, so every fact that distinguishes two of them — the subject,
 * the date, the sender, the path — is a short scalar, and short scalars in
 * columns are the fastest thing in the world to compare. A card per email would
 * be a table row with more whitespace and worse alignment. The package is the
 * opposite: it carries a document and a letter, which is what its cards
 * show.</p>
 *
 * <p><b>Chrome appears when there is enough content to need it</b> — Table and
 * Tile Standard §5. A submission accumulates a handful of intro emails across
 * its life, so in practice this renders the styled table and nothing else;
 * `SubTable` makes that decision from the row count rather than leaving it to
 * be re-argued per table. Its search and sort are honest here because the
 * caller holds every version there is: `SendHistory` reads the whole list in
 * one call, so the controls act on the complete set rather than on a slice
 * pretending to be one.</p>
 *
 * <p>🔴 <b>THIS TABLE USED TO CLAIM IT DID THIS ALREADY, AND IT DID NOT.</b>
 * The comment that stood here said the header treatment was resolved by the
 * surface containing it — "tinted inside a card or a drawer and full onyx on a
 * bare page with no prop and no branch". That is what §4.3 asks for and it is
 * not what the code did: the shared `Table`/`THead`/`TH` primitives do not
 * consult {@link Surface} at all. `THead` is hard-coded `bg-onyx` and `TH`
 * `text-gold`, so this painted the PAGE's own full-onyx header — in gold at
 * 10px, which is 4.0:1 and below AA — inside the `DetailSection` card it
 * actually lives in, on every surface, always.</p>
 *
 * <p>It is worth naming the failure mode rather than just fixing it: the file
 * imported from the design system and rendered the shared components, so every
 * check that asks "is this using our primitives?" passed it, and the comment
 * asserting the behaviour made a reader less likely to look. Reaching the
 * behaviour needs `SubTable`, which is the thing that reads the surface.</p>
 */
const BUYER_INTRO_HISTORY_CAPTION =
  "Intro emails sent to the buyer, newest first. Select a version to read it.";

export const BROKER_INTRO_HISTORY_CAPTION =
  "Intro emails sent to the listing broker, newest first. Select a version to read it.";

export function IntroEmailVersionTable({
  versions,
  selectedId,
  onSelect,
  caption = BUYER_INTRO_HISTORY_CAPTION,
}: {
  versions: BuyerSendVersionDto[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /**
   * The screen-reader caption. `SubTable` renders it `sr-only`, so it is the
   * ONLY thing that tells a non-sighted reader whose history this table holds —
   * and this component serves BOTH intro emails now.
   *
   * <p>🔴 It was hard-coded to the buyer's wording, so a screen-reader user
   * inside the "Sent to the listing broker" section was told the table held
   * emails sent to the buyer. The visible heading above it said otherwise; the
   * caption is the half nobody looks at, which is exactly why it went wrong and
   * why nothing caught it.</p>
   *
   * <p>The DEFAULT is the buyer's wording verbatim, so the buyer's history is
   * byte-identical to what it rendered before this prop existed. Only the broker
   * call site passes anything.</p>
   */
  caption?: string;
}) {
  return (
    <SubTable<BuyerSendVersionDto>
      caption={caption}
      columns={INTRO_EMAIL_COLUMNS}
      rows={versions}
      rowKey={(v) => v.id}
      // The row is the control. A separate "view" button in its own column
      // would cost horizontal budget the subject needs, and a reader comparing
      // versions clicks the row anyway. `SubTable` supplies the keyboard half —
      // role, tab stop and Enter/Space — so a row is never mouse-only.
      onRowClick={(v) => onSelect(v.id)}
      rowSelected={(v) => v.id === selectedId}
      data-testid="intro-email-versions"
    />
  );
}

const INTRO_EMAIL_COLUMNS: SubTableColumn<BuyerSendVersionDto>[] = [
  {
    key: "version",
    header: "Version",
    className: "whitespace-nowrap font-medium",
    searchText: (v) => [versionLabel(v), combinedOthersLabel(v) ?? ""].join(" "),
    render: (v) => {
      const combined = combinedOthersLabel(v);
      return (
        <span className="inline-flex flex-wrap items-center gap-space-2">
          {versionLabel(v)}
          {/* Inline after the row's own name, never its own column (§6.3) — a
              dedicated column would be empty on every ordinary row. */}
          {v.recordedBeforeVersioning ? <CarriedInBadge /> : null}
          {combined ? (
            <span className="text-caption text-gray-mid font-normal" data-testid="send-version-combined">
              {combined}
            </span>
          ) : null}
        </span>
      );
    },
  },
  {
    key: "subject",
    header: "Subject",
    className: "min-w-0",
    searchText: (v) => v.subject ?? "",
    render: (v) => (v.subject ? <span className="block truncate">{v.subject}</span> : <Blank />),
  },
  {
    key: "sent",
    header: "Sent",
    className: "whitespace-nowrap",
    sortAccessor: (v) => v.sentAtUtc,
    render: (v) => <Timestamp value={v.sentAtUtc} />,
  },
  {
    key: "by",
    header: "By",
    className: "whitespace-nowrap",
    searchText: (v) => v.sentByDisplayName ?? v.sentByUserId ?? "",
    render: (v) => v.sentByDisplayName ?? (v.sentByUserId ? v.sentByUserId : <Blank />),
  },
  {
    key: "path",
    header: "Path",
    className: "whitespace-nowrap",
    render: (v) =>
      v.selfAttested === null ? (
        // "Not recorded" is a state, not a value — muted prose rather than a
        // pill (§6.1), and no door, because a path nobody captured in 2025 is
        // not work anyone can pick up.
        <span className="text-gray-mid italic">Not recorded</span>
      ) : (
        pathLabel(v)
      ),
  },
];
